import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Stack,
    Step,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import '@mediapipe/face_detection'
import '@tensorflow/tfjs-core'
import '@tensorflow/tfjs-backend-webgl'
import * as faceDetection from '@tensorflow-models/face-detection'
import { ImageInterface } from '../../pages/Accounts/Register'
import { convertBase64ToFile } from '../../utils/maks'
import { useTranslation } from 'react-i18next'

interface Coordinate {
    x: number
    y: number
}

enum Steps {
    TakePhoto = 1,
    ConfirmPhoto = 2,
}

interface Props {
    open: boolean
    handleClose(v: boolean): void
    returnImage(image: ImageInterface): void
    type: 'register' | 'login'
}

const IdealMinCoordinate: Coordinate = {
    x: 215,
    y: 165,
}

const IdealMaxCoordinate: Coordinate = {
    x: 440,
    y: 390,
}

const FACE_DETECTED_LOGIN_THRESHOLD = 20

export function FaceCameraModal({
    open,
    handleClose,
    returnImage,
    type,
}: Props) {
    const {t} = useTranslation()
    const videoRef = useRef<HTMLVideoElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const canvasConfirmRef = useRef<HTMLCanvasElement>(null)
    const stream = useRef<MediaStream>()
    const detectorInterval = useRef<NodeJS.Timeout>()
    const detector = useRef<faceDetection.FaceDetector>()
    const [faceDetected, setFaceDetected] = useState(false)
    const faceDetectedCount = useRef(0)
    const [step, setStep] = useState(Steps.TakePhoto)

    useEffect(() => {
        if (step === Steps.TakePhoto) {
            detectorInterval.current = setInterval(detectFaces, 100)
        } else {
            clearInterval(detectorInterval.current)
        }

        return () => {
            clearInterval(detectorInterval.current)
        }
    }, [step])

    useEffect(() => {
        setTimeout(() => {
            startCamera()
            createDetector()
        }, 100)

        return () => {
            stopCamera()
        }
    }, [])

    const handleReturnImage = async () => {
        if (!canvasConfirmRef.current) return

        const base64 = canvasConfirmRef.current?.toDataURL()
        const file = await convertBase64ToFile(base64)

        returnImage({
            content: base64,
            file: file,
            isBase64: true,
        })
    }

    const compareCoordinates = (
        coord1: Coordinate,
        coord2: Coordinate
    ): number => {
        const distanceSquare =
            Math.pow(coord1.x - coord2.x, 2) + Math.pow(coord1.y - coord2.y, 2)
        return Math.sqrt(distanceSquare)
    }

    const detectFaces = async () => {
        if (!videoRef.current) return

        const canvas = document.createElement('canvas')
        canvas.width = videoRef.current.videoWidth
        canvas.height = videoRef.current.videoHeight

        const ctx = canvas.getContext('2d')

        ctx?.scale(-1, 1)
        ctx?.drawImage(videoRef.current, canvas.width * -1, 0)

        const res = await detector.current?.estimateFaces(canvas, {
            flipHorizontal: false,
        })

        if (!res || !res.length) {
            setFaceDetected(false)
            drawCanvas(false)

            faceDetectedCount.current = 0

            return
        }

        const minDistanceOk =
            compareCoordinates(
                { x: res[0].box.xMin, y: res[0].box.yMin },
                { x: IdealMinCoordinate.x, y: IdealMinCoordinate.y }
            ) <= 50

        const maxDistanceOk =
            compareCoordinates(
                { x: res[0].box.xMax, y: res[0].box.yMax },
                { x: IdealMaxCoordinate.x, y: IdealMaxCoordinate.y }
            ) <= 50

        const isFaceDetected = minDistanceOk && maxDistanceOk

        setFaceDetected(isFaceDetected)
        drawCanvas(isFaceDetected)

        if (canvasConfirmRef.current) {
            canvasConfirmRef.current.width = canvas.width
            canvasConfirmRef.current.height = canvas.height

            const ctx = canvasConfirmRef.current.getContext('2d')

            ctx?.drawImage(canvas, 0, 0)
        }

        if (isFaceDetected) faceDetectedCount.current += 1
        else faceDetectedCount.current = 0

        if (
            faceDetectedCount.current === FACE_DETECTED_LOGIN_THRESHOLD &&
            type === 'login'
        ) {
            await handleReturnImage()
        }
    }

    const drawCanvas = (faceDetected: boolean) => {
        if (!videoRef.current || !canvasRef.current) return

        canvasRef.current.width = videoRef.current.videoWidth
        canvasRef.current.height = videoRef.current.videoHeight

        const ctx = canvasRef.current.getContext('2d')
        if (ctx) {
            ctx.clearRect(
                0,
                0,
                canvasRef.current.width,
                canvasRef.current.height
            )
            ctx.beginPath()
            ctx.strokeStyle = '#00FF00'

            if (faceDetected) {
                ctx.ellipse(325, 238, 105, 145, 0, 0, 2 * Math.PI)
            }

            ctx.stroke()
        }
    }

    const startCamera = async () => {
        if (!videoRef.current) return

        stream.current = await navigator.mediaDevices.getUserMedia({
            video: {
                width: 640,
                height: 480,
            },
        })

        videoRef.current.srcObject = stream.current
        videoRef.current.play()
    }

    const stopCamera = () => {
        if (videoRef.current) {
            videoRef.current.srcObject = null
            videoRef.current.pause()
        }

        stream.current &&
            stream.current.getTracks().forEach((track) => track.stop())
    }

    const createDetector = async () => {
        const model = faceDetection.SupportedModels.MediaPipeFaceDetector
        const detectorConfig:
            | faceDetection.MediaPipeFaceDetectorMediaPipeModelConfig
            | faceDetection.MediaPipeFaceDetectorTfjsModelConfig = {
            runtime: 'tfjs',
        }

        if (detector.current) {
            await detector.current.dispose()
        }

        detector.current = await faceDetection.createDetector(
            model,
            detectorConfig
        )
    }

    return (
        <Dialog
            open={open}
            onClose={() => handleClose(false)}
            maxWidth="lg"
            className="modal"
        >
            <DialogTitle>
                <Box
                    sx={{
                        color: 'text.primary',
                        fontSize: '20px',
                        fontWeight: '500',
                        textAlign: 'center',
                        borderBottom: '1px solid rgba(218, 218, 218, 0.7)',
                        paddingBottom: '10px',
                        minWidth: { xs: 'auto', sm: '350px' },
                    }}
                >
                    {type === 'register' ? t('Registrar cara') : t('Entra con cara')}
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box
                    overflow={'hidden'}
                    display={step === Steps.TakePhoto ? 'block' : 'none'}
                >
                    <Box
                        position={'relative'}
                        width={`${videoRef.current?.videoWidth}px`}
                    >
                        <video
                            ref={videoRef}
                            style={{
                                width: `${videoRef.current?.videoWidth}px`,
                                height: `${videoRef.current?.videoHeight}px`,
                                objectFit: 'fill',
                                transform: 'rotateY(180deg)',
                                zIndex: '-1',
                            }}
                        />
                        <Box
                            component={'img'}
                            src="/filtro-selfie.png"
                            position={'absolute'}
                            sx={{
                                zIndex: '1',
                                width: `${videoRef.current?.videoWidth}px`,
                                height: `${videoRef.current?.videoHeight}px`,
                                top: '0',
                                left: '0',
                            }}
                        />
                        <Box
                            component={'canvas'}
                            position={'absolute'}
                            zIndex={'2'}
                            ref={canvasRef}
                            top={'0'}
                            left={'0'}
                        />
                    </Box>
                    {type === 'register' && (
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                width: '100%',
                                textTransform: 'none',
                                p: '15px',
                                mt: '15px',
                                zIndex: '3',
                            }}
                            disabled={!faceDetected}
                            onClick={() => setStep(Steps.ConfirmPhoto)}
                        >
                            {t('Confirmar')}
                        </Button>
                    )}
                </Box>
                <Box
                    overflow={'hidden'}
                    display={step === Steps.ConfirmPhoto ? 'block' : 'none'}
                >
                    <Stack direction={'column'} alignItems={'center'}>
                        <Box component={'canvas'} ref={canvasConfirmRef} />
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                width: '100%',
                                textTransform: 'none',
                                p: '15px',
                                mt: '15px',
                            }}
                            onClick={handleReturnImage}
                        >
                            {t('Confirmar')}
                        </Button>
                        <Button
                            variant="text"
                            color="primary"
                            sx={{
                                width: '100%',
                                textTransform: 'none',
                                p: '15px',
                                mt: '15px',
                            }}
                            onClick={() => setStep(Steps.TakePhoto)}
                        >
                            {t('Volver')}
                        </Button>
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    )
}
